<div class="cb-record-documentation">
    <!-- Header -->
    <div class="flex justify-content-between align-items-center mb-4">
        <div>
            <h2 class="m-0">Record Documentation Generator</h2>
            <p class="text-color-secondary mt-1 mb-0">
                Generate AI-powered documentation for configuration entity records
            </p>
        </div>
        <div class="flex gap-2">
            <p-button
                icon="fa-regular fa-refresh"
                label="Refresh"
                [outlined]="true"
                (onClick)="refresh()"
                [loading]="loading">
            </p-button>
        </div>
    </div>

    <!-- Summary Cards -->
    <div class="grid mb-4">
        <div class="col-12 md:col-6 lg:col-2">
            <div class="surface-card shadow-1 p-3 border-round">
                <div class="text-color-secondary text-sm mb-1">Total Entities</div>
                <div class="text-2xl font-semibold">{{ summary.totalEntities }}</div>
            </div>
        </div>
        <div class="col-12 md:col-6 lg:col-2">
            <div class="surface-card shadow-1 p-3 border-round">
                <div class="text-color-secondary text-sm mb-1">Total Records</div>
                <div class="text-2xl font-semibold">{{ summary.totalRecords }}</div>
            </div>
        </div>
        <div class="col-12 md:col-6 lg:col-2">
            <div class="surface-card shadow-1 p-3 border-round">
                <div class="text-color-secondary text-sm mb-1">Documented</div>
                <div class="text-2xl font-semibold text-green-500">{{ summary.documentedRecords }}</div>
            </div>
        </div>
        <div class="col-12 md:col-6 lg:col-2">
            <div class="surface-card shadow-1 p-3 border-round">
                <div class="text-color-secondary text-sm mb-1">Coverage</div>
                <div class="text-2xl font-semibold">{{ formatCoverage(summary.overallCoveragePercentage) }}</div>
            </div>
        </div>
        <div class="col-12 md:col-6 lg:col-2">
            <div class="surface-card shadow-1 p-3 border-round">
                <div class="text-color-secondary text-sm mb-1">Fully Documented</div>
                <div class="text-2xl font-semibold text-green-500">{{ summary.fullyDocumentedEntities }}</div>
            </div>
        </div>
        <div class="col-12 md:col-6 lg:col-2">
            <div class="surface-card shadow-1 p-3 border-round">
                <div class="text-color-secondary text-sm mb-1">Not Documented</div>
                <div class="text-2xl font-semibold text-orange-500">{{ summary.entitiesWithNoDocumentation }}</div>
            </div>
        </div>
    </div>

    <div class="grid">
        <!-- Left Panel: Entity Selection -->
        <div class="col-12 lg:col-8">
            <div class="surface-card shadow-1 p-4 border-round">
                <div class="flex justify-content-between align-items-center mb-3">
                    <h3 class="m-0">Configuration Entities</h3>
                    <div class="flex gap-2 align-items-center">
                        <p-select
                            [(ngModel)]="selectedModule"
                            [options]="modules"
                            placeholder="Filter by Module"
                            [showClear]="true"
                            (onChange)="onModuleChange()"
                            styleClass="w-12rem">
                        </p-select>
                        <p-button
                            icon="fa-regular fa-check-double"
                            label="Select All"
                            [outlined]="true"
                            size="small"
                            (onClick)="selectAll()">
                        </p-button>
                        <p-button
                            icon="fa-regular fa-times"
                            label="Clear"
                            [outlined]="true"
                            size="small"
                            (onClick)="clearSelection()">
                        </p-button>
                    </div>
                </div>

                <p-table
                    [value]="filteredEntities"
                    [(selection)]="selectedEntities"
                    dataKey="objectTypeAlias"
                    [loading]="loading"
                    [paginator]="true"
                    [rows]="10"
                    [rowsPerPageOptions]="[10, 25, 50]"
                    styleClass="p-datatable-sm">
                    <ng-template pTemplate="header">
                        <tr>
                            <th style="width: 3rem">
                                <p-tableHeaderCheckbox></p-tableHeaderCheckbox>
                            </th>
                            <th>Entity</th>
                            <th>Module</th>
                            <th style="text-align: center">Records</th>
                            <th style="text-align: center">Documented</th>
                            <th style="text-align: center">Coverage</th>
                            <th style="width: 4rem"></th>
                        </tr>
                    </ng-template>
                    <ng-template pTemplate="body" let-entity>
                        <tr>
                            <td>
                                <p-tableCheckbox [value]="entity"></p-tableCheckbox>
                            </td>
                            <td>
                                <div class="font-semibold">{{ entity.entityName }}</div>
                                <div class="text-color-secondary text-sm">{{ entity.objectTypeAlias }}</div>
                            </td>
                            <td>
                                <p-tag [value]="entity.moduleName" [severity]="'info'"></p-tag>
                            </td>
                            <td style="text-align: center">{{ entity.totalRecords }}</td>
                            <td style="text-align: center">{{ entity.documentedRecords }}</td>
                            <td style="text-align: center">
                                <p-tag
                                    [value]="formatCoverage(entity.coveragePercentage)"
                                    [severity]="getCoverageSeverity(entity.coveragePercentage)">
                                </p-tag>
                            </td>
                            <td>
                                <p-button
                                    icon="fa-regular fa-robot"
                                    [rounded]="true"
                                    [text]="true"
                                    size="small"
                                    pTooltip="Generate for this entity"
                                    (onClick)="generateForEntity(entity)"
                                    [disabled]="generating">
                                </p-button>
                            </td>
                        </tr>
                    </ng-template>
                    <ng-template pTemplate="emptymessage">
                        <tr>
                            <td colspan="7" class="text-center p-4 text-color-secondary">
                                <i class="fa-regular fa-folder-open text-4xl mb-2"></i>
                                <div>No configuration entities found</div>
                            </td>
                        </tr>
                    </ng-template>
                </p-table>
            </div>
        </div>

        <!-- Right Panel: Generation Options -->
        <div class="col-12 lg:col-4">
            <div class="surface-card shadow-1 p-4 border-round mb-3">
                <h3 class="mt-0 mb-3">Generation Options</h3>

                <div class="mb-3">
                    <label class="block mb-2 font-semibold">Selected Entities</label>
                    <div class="text-2xl">{{ selectedEntities.length }} entities</div>
                    <div class="text-color-secondary">{{ selectedRecordCount }} total records</div>
                </div>

                <p-divider></p-divider>

                <div class="flex flex-column gap-3">
                    <div class="flex align-items-center">
                        <p-checkbox
                            [(ngModel)]="overwriteExisting"
                            [binary]="true"
                            inputId="overwrite">
                        </p-checkbox>
                        <label for="overwrite" class="ml-2">
                            Overwrite existing (non-locked)
                        </label>
                    </div>

                    <div class="flex align-items-center">
                        <p-checkbox
                            [(ngModel)]="includeTechnicalDescription"
                            [binary]="true"
                            inputId="technical">
                        </p-checkbox>
                        <label for="technical" class="ml-2">
                            Include technical description
                        </label>
                    </div>

                    <div class="flex align-items-center">
                        <p-checkbox
                            [(ngModel)]="includeUsageNotes"
                            [binary]="true"
                            inputId="usage">
                        </p-checkbox>
                        <label for="usage" class="ml-2">
                            Include usage notes
                        </label>
                    </div>
                </div>

                <p-divider></p-divider>

                <div class="mb-3">
                    <label class="block mb-2 font-semibold">Additional Context</label>
                    <textarea
                        pTextarea
                        [(ngModel)]="additionalContext"
                        [rows]="3"
                        placeholder="Optional: Provide context for better documentation (e.g., 'Insurance management system')"
                        class="w-full">
                    </textarea>
                </div>

                <div class="mb-3">
                    <label class="block mb-2 font-semibold">Max Records per Entity</label>
                    <p-inputNumber
                        [(ngModel)]="maxRecordsPerEntity"
                        [min]="0"
                        [showButtons]="true"
                        placeholder="0 = all records"
                        styleClass="w-full">
                    </p-inputNumber>
                    <small class="text-color-secondary">0 = process all records</small>
                </div>

                <p-button
                    icon="fa-regular fa-robot"
                    label="Generate Documentation"
                    styleClass="w-full"
                    (onClick)="generateDocumentation()"
                    [loading]="generating"
                    [disabled]="selectedEntities.length === 0">
                </p-button>
            </div>

            <!-- Results Panel -->
            <div class="surface-card shadow-1 p-4 border-round" *ngIf="showResults && lastResult">
                <div class="flex justify-content-between align-items-center mb-3">
                    <h3 class="m-0">Generation Results</h3>
                    <p-button
                        icon="fa-regular fa-times"
                        [rounded]="true"
                        [text]="true"
                        size="small"
                        (onClick)="closeResults()">
                    </p-button>
                </div>

                <div class="grid mb-3">
                    <div class="col-6">
                        <div class="text-color-secondary text-sm">Processed</div>
                        <div class="text-xl font-semibold">{{ lastResult.totalProcessed }}</div>
                    </div>
                    <div class="col-6">
                        <div class="text-color-secondary text-sm">Documented</div>
                        <div class="text-xl font-semibold text-green-500">{{ lastResult.successfullyDocumented }}</div>
                    </div>
                    <div class="col-6">
                        <div class="text-color-secondary text-sm">Skipped</div>
                        <div class="text-xl font-semibold text-orange-500">{{ lastResult.skipped }}</div>
                    </div>
                    <div class="col-6">
                        <div class="text-color-secondary text-sm">Failed</div>
                        <div class="text-xl font-semibold text-red-500">{{ lastResult.failed }}</div>
                    </div>
                </div>

                <div *ngIf="lastResult.errors && lastResult.errors.length > 0" class="mb-3">
                    <p-message severity="error" *ngFor="let error of lastResult.errors">
                        {{ error }}
                    </p-message>
                </div>

                <p-accordion *ngIf="lastResult.entityResults && lastResult.entityResults.length > 0" value="0">
                    <p-accordion-panel
                        *ngFor="let result of lastResult.entityResults; let i = index"
                        [value]="i.toString()">
                        <p-accordion-header>
                            {{ result.objectTypeAlias }} ({{ result.documented }}/{{ result.totalRecords }})
                        </p-accordion-header>
                        <p-accordion-content>
                            <div class="text-sm">
                                <div>Total: {{ result.totalRecords }}</div>
                                <div class="text-green-500">Documented: {{ result.documented }}</div>
                                <div class="text-orange-500">Skipped: {{ result.skipped }}</div>
                                <div class="text-red-500" *ngIf="result.failed > 0">Failed: {{ result.failed }}</div>
                                <div *ngIf="result.recordNames && result.recordNames.length > 0" class="mt-2">
                                    <strong>Documented Records:</strong>
                                    <ul class="pl-3 mt-1">
                                        <li *ngFor="let name of result.recordNames.slice(0, 10)">{{ name }}</li>
                                        <li *ngIf="result.recordNames.length > 10">
                                            ... and {{ result.recordNames.length - 10 }} more
                                        </li>
                                    </ul>
                                </div>
                            </div>
                        </p-accordion-content>
                    </p-accordion-panel>
                </p-accordion>
            </div>
        </div>
    </div>
</div>
